import { getToken, clearToken } from "./authService";

const API_BASE =
  process.env.REACT_APP_API_BASE || "https://indoor-nav-api.ngrok.dev/api";

export const STATIC_BASE =
  process.env.REACT_APP_STATIC_BASE || "https://indoor-nav-api.ngrok.dev";

const PUBLIC_BASE_URL = (
  process.env.REACT_APP_PUBLIC_BASE_URL ||
  (typeof window !== "undefined" ? window.location.origin : "")
).replace(/\/+$/, "");
// const API_BASE =
//   process.env.REACT_APP_API_BASE || "http://192.168.1.131:3000/api";

// export const STATIC_BASE =
//   process.env.REACT_APP_STATIC_BASE || "http://192.168.1.131:3000";
// const API_BASE =
//   process.env.REACT_APP_API_BASE || "http://localhost:3000/api";

// export const STATIC_BASE =
//   process.env.REACT_APP_STATIC_BASE || "http://localhost:3000";
export class ApiError extends Error {
  constructor(message, status, code, details) {
    super(message);
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

async function request(path, { method = "GET", body, headers, isForm, skipAuth } = {}) {
  const token = getToken();
  const finalHeaders = { ...(headers || {}) };
  if (token && !skipAuth) finalHeaders.Authorization = `Bearer ${token}`;

  let payload = body;
  if (body && !isForm) {
    finalHeaders["Content-Type"] = "application/json";
    payload = JSON.stringify(body);
  }

  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: finalHeaders,
    body: payload,
  });

  if (res.status === 401) {
    clearToken();
  }

  const text = await res.text();
  const json = text ? JSON.parse(text) : null;

  if (!res.ok) {
    const err = json?.error || {};
    throw new ApiError(
      err.message || res.statusText,
      res.status,
      err.code,
      err.details,
    );
  }

  return json?.data ?? json;
}

// helper สำหรับ multipart/form-data
function toForm(obj) {
  const fd = new FormData();
  Object.entries(obj || {}).forEach(([k, v]) => {
    if (v === undefined || v === null) return;
    fd.append(k, v);
  });
  return fd;
}

// =========================
// Auth
// =========================
export const AuthAPI = {
  login: (email, password) =>
    request("/auth/login", { method: "POST", body: { email, password } }),
  me: () => request("/auth/me"),
  profile: () => request("/auth/me"),
};

// =========================
// Buildings
// =========================
export const BuildingAPI = {
  list: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/buildings${qs ? `?${qs}` : ""}`);
  },
  get: (id) => request(`/buildings/${id}`),
  create: (data) =>
    request("/buildings", { method: "POST", body: toForm(data), isForm: true }),
  update: (id, data) =>
    request(`/buildings/${id}`, {
      method: "PUT",
      body: toForm(data),
      isForm: true,
    }),
  remove: (id) => request(`/buildings/${id}`, { method: "DELETE" }),
  setStatus: (id, status) =>
    request(`/buildings/${id}/status`, { method: "PATCH", body: { status } }),
};

// =========================
// Floors
// =========================
export const FloorAPI = {
  listByBuilding: (buildingId) =>
    request(`/buildings/${buildingId}/floors`),
  get: (id) =>
    request(`/floors/${id}`),
  create: (buildingId, data) =>
    request(`/buildings/${buildingId}/floors`, {
      method: "POST",
      body: toForm(data),
      isForm: true,
    }),
  update: (id, data) =>
    request(`/floors/${id}`, {
      method: "PUT",
      body: data?.image ? toForm(data) : data,
      isForm: !!data?.image,
    }),
  remove: (id) => request(`/floors/${id}`, { method: "DELETE" }),
};

// =========================
// Destinations
// =========================
export const DestinationAPI = {
  listByFloor: (floorId) => request(`/floors/${floorId}/destinations`),
  create: (floorId, data) =>
    request(`/floors/${floorId}/destinations`, {
      method: "POST",
      body: data?.image ? toForm(data) : data,
      isForm: !!data?.image,
    }),
  update: (id, data) =>
    request(`/destinations/${id}`, {
      method: "PUT",
      body: data?.image ? toForm(data) : data,
      isForm: !!data?.image,
    }),
  remove: (id) => request(`/destinations/${id}`, { method: "DELETE" }),
};

// =========================
// QR points
// =========================
export const QrAPI = {
  // master list (admin) — รวม QR ทุก floor/building, filter + paginate
  list: (params = {}) => {
    const qs = new URLSearchParams(
      Object.fromEntries(
        Object.entries(params).filter(([, v]) => v !== undefined && v !== "")
      )
    ).toString();
    return request(`/qr${qs ? `?${qs}` : ""}`);
  },
  get: (id) => request(`/qr/${id}`),
  listByFloor: (floorId) => request(`/floors/${floorId}/qr`),
  create: (floorId, data) =>
    request(`/floors/${floorId}/qr`, {
      method: "POST",
      body: withQrPublicUrl(data),
    }),
  update: (id, data) =>
    request(`/qr/${id}`, { method: "PUT", body: withQrPublicUrl(data) }),
  remove: (id) => request(`/qr/${id}`, { method: "DELETE" }),
  setStatus: (id, status) =>
    request(`/qr/${id}/status`, { method: "PATCH", body: { status } }),
  getByCode: (code) => request(`/qr/by-code/${encodeURIComponent(code)}`),
  scan: (id, sessionId, userAgent) =>
    request(`/qr/${id}/scan`, {
      method: "POST",
      body: { userSessionId: sessionId, userAgent },
    }),
  scanRoute: (id, payload) =>
    request(`/qr/${id}/scan/route`, { method: "POST", body: payload }),
  analytics: (id) => request(`/qr/${id}/analytics`),
  scanLogs: (id, params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/qr/${id}/scan-logs${qs ? `?${qs}` : ""}`);
  },
  // ⬇ URL ตรงๆ (ไม่ผ่าน wrapper) สำหรับ <img src=...> และปุ่ม download
  imageUrl: (id, size = 512, code) => {
    const qs = new URLSearchParams({ size: String(size) });
    appendQrPublicParams(qs, code);
    return `${API_BASE}/qr/${id}/image?${qs.toString()}`;
  },
  downloadUrl: (id, code) => {
    const qs = new URLSearchParams();
    appendQrPublicParams(qs, code);
    return `${API_BASE}/qr/${id}/download${qs.toString() ? `?${qs.toString()}` : ""}`;
  },
};

function buildScanUrl(code) {
  if (!PUBLIC_BASE_URL || !code) return undefined;
  return `${PUBLIC_BASE_URL}/scan/${encodeURIComponent(code)}`;
}

function withQrPublicUrl(data = {}) {
  const scanUrl = buildScanUrl(data.code);
  if (!scanUrl) return data;
  return {
    ...data,
    scanUrl,
    targetUrl: scanUrl,
    publicBaseUrl: PUBLIC_BASE_URL,
  };
}

function appendQrPublicParams(qs, code) {
  if (PUBLIC_BASE_URL) qs.set("publicBaseUrl", PUBLIC_BASE_URL);
  const scanUrl = buildScanUrl(code);
  if (scanUrl) {
    qs.set("scanUrl", scanUrl);
    qs.set("targetUrl", scanUrl);
  }
}

// =========================
// Shapes (MapDrawing)
// =========================
export const ShapeAPI = {
  listByFloor: (floorId) => request(`/floors/${floorId}/shapes`),
  create: (floorId, data) =>
    request(`/floors/${floorId}/shapes`, { method: "POST", body: data }),
  bulkSave: (floorId, shapes) =>
    request(`/floors/${floorId}/shapes/bulk`, {
      method: "POST",
      body: { shapes },
    }),
  update: (id, data) =>
    request(`/shapes/${id}`, { method: "PUT", body: data }),
  remove: (id) => request(`/shapes/${id}`, { method: "DELETE" }),
};

// =========================
// Navigation (mobile user)
// =========================
export const NavigationAPI = {
  getFloor: (floorId) => request(`/navigation/floors/${floorId}`),
  computePath: ({ floorId, startNodeId, endNodeId, start, end }) =>
    request("/navigation/path", {
      method: "POST",
      body: {
        floorId,
        ...(startNodeId ? { startNodeId } : {}),
        ...(endNodeId ? { endNodeId } : {}),
        ...(start ? { start } : {}),
        ...(end ? { end } : {}),
      },
    }),
  computeRoute: (fromQrId, toDestinationId) =>
    request("/navigation/route", {
      method: "POST",
      body: { fromQrId, toDestinationId },
    }),
  logRoute: (payload) =>
    request("/navigation/route-logs", {
      method: "POST",
      body: payload,
      skipAuth: true,
    }),
};

// =========================
// Navigation path management (admin)
// =========================
export const NavigationPathAPI = {
  createEdge: (data) =>
    request("/navigation/edges", {
      method: "POST",
      body: data,
    }),
  listByFloor: (floorId) => request(`/navigation/floors/${floorId}/edges`),
  removeEdge: (id) => request(`/navigation/edges/${id}`, { method: "DELETE" }),
};

// =========================
// Navigation graph/corridor management (admin)
// =========================
export const NavigationGraphAPI = {
  listCorridors: (floorId) => request(`/navigation/floors/${floorId}/corridors`),
  saveCorridors: (floorId, corridors) =>
    request(`/navigation/floors/${floorId}/corridors/bulk`, {
      method: "POST",
      body: { corridors },
    }),
  generateGraph: (floorId, corridors = undefined) =>
    request(`/navigation/floors/${floorId}/graph/generate`, {
      method: "POST",
      body: corridors ? { corridors } : undefined,
    }),
  getGraph: (floorId) => request(`/navigation/floors/${floorId}/graph`),
};

// =========================
// Feedback
// =========================
export const FeedbackAPI = {
  submit: (data) => {
    // ถ้ามี closure (object) → stringify ก่อนใส่ใน FormData
    const payload = { ...data };
    if (payload.closure && typeof payload.closure === "object") {
      payload.closure = JSON.stringify(payload.closure);
    }
    return request("/feedback", {
      method: "POST",
      body: toForm(payload),
      isForm: true,
    });
  },
  list: (params = {}) => {
    const q = params.q || "";
    const status = params.status || "all";
    const page = params.page || 1;
    const limit = params.limit || 10;
    return request(`/feedback?q=${encodeURIComponent(q)}&status=${status}&page=${page}&limit=${limit}`);
  },
  get: (id) => request(`/feedback/${id}`),
  setStatus: (id, status) =>
    request(`/feedback/${id}/status`, { method: "PATCH", body: { status } }),
  confirmClosure: (id) =>
    request(`/feedback/${id}/closure/confirm`, { method: "PATCH" }),
  rejectClosure: (id) =>
    request(`/feedback/${id}/closure/reject`, { method: "PATCH" }),
  remove: (id) => request(`/feedback/${id}`, { method: "DELETE" }),
};

// =========================
// Path Closures (ทางปิด — แจ้งจาก user, routing เลี่ยงให้)
// =========================
export const PathClosureAPI = {
  listByFloor: (floorId, status = "active") =>
    request(`/floors/${floorId}/path-closures?status=${status}`),
  get: (id) => request(`/path-closures/${id}`),
  resolve: (id) =>
    request(`/path-closures/${id}/resolve`, { method: "PATCH" }),
  remove: (id) => request(`/path-closures/${id}`, { method: "DELETE" }),
};

// =========================
// Dashboard
// =========================
function toQuery(params = {}) {
  const qs = new URLSearchParams(
    Object.fromEntries(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== "")
    )
  ).toString();
  return qs ? `?${qs}` : "";
}

export const DashboardAPI = {
  overview: (params = {}) => request(`/dashboard/overview${toQuery(params)}`),
  summary: () => request("/dashboard/summary"),
  qrStats: () => request("/dashboard/qr-stats"),
  getLiveMap: () => request("/dashboard/live-map"),
  feedbackStats: async () => {
    try {
      const kpi = await request("/feedback/kpi");
      return {
        total: kpi.totalFeedback,
        totalDelta: kpi.totalFeedbackDelta,
        totalDeltaTone: kpi.totalFeedbackDeltaTone,
        doneToday: kpi.resolvedToday,
        pending: kpi.pendingCount,
      };
    } catch (error) {
      console.warn("DashboardAPI.feedbackStats failed:", error);
      throw error;
    }
  },
  topRoutesAnalytics: (params = {}) => {
    const qs = new URLSearchParams(
      Object.fromEntries(
        Object.entries(params).filter(([, v]) => v !== undefined && v !== "")
      )
    ).toString();
    return request(`/dashboard/top-routes-analytics${qs ? `?${qs}` : ""}`);
  },
  hourlyDensity: (period = "24h") =>
    request(`/dashboard/hourly-density?period=${period}`),
};

export const api = {
  getFeedbackChart: (period = "weekly") =>
    request(`/feedback/chart?period=${period}`),
};

// แปลง path รูปจาก backend (เช่น "/static/xxx.jpg") เป็น URL เต็ม
export function resolveAsset(url) {
  if (!url) return url;
  if (/^(https?|blob|data):/i.test(url)) return url;
  return `${STATIC_BASE}${url.startsWith("/") ? "" : "/"}${url}`;
}
